import { AppDispatch } from "../store/store";
import {
  getPortalDataFhpl,
  getPortalDataHealthIndia,
  getPortalDataHeritage,
} from "../models/preAuthSlice";

export const unwrapPortalNode = (node: unknown): Record<string, unknown> | null => {
  if (node == null || typeof node !== "object") {
    return null;
  }
  const record = node as Record<string, unknown>;
  const inner = record.data;
  if (inner != null && typeof inner === "object" && !Array.isArray(inner)) {
    return inner as Record<string, unknown>;
  }
  return record;
};

export const getPrimaryPortalName = (casesResponse?: {
  portalMetaDataNames?: string[];
  schemeMetaDataName?: string;
}) =>
  casesResponse?.portalMetaDataNames?.[0] ??
  casesResponse?.schemeMetaDataName ??
  "";

export const isHeritagePortalScheme = (casesResponse?: {
  portalMetaDataNames?: string[];
  schemeMetaDataName?: string;
}) => getPrimaryPortalName(casesResponse).toUpperCase().includes("HERITAGE");

export const isHealthIndiaPortalScheme = (casesResponse?: {
  portalMetaDataNames?: string[];
  schemeMetaDataName?: string;
}) =>
  getPrimaryPortalName(casesResponse).toUpperCase().includes("HEALTH INDIA");

export const isFhplPortalScheme = (casesResponse?: {
  portalMetaDataNames?: string[];
  schemeMetaDataName?: string;
}) => getPrimaryPortalName(casesResponse).toUpperCase().includes("FHPL");

export const resolveHeritageHealthIndiaClaimId = (
  portalData?: Record<string, unknown> | null,
): string | null => {
  if (!portalData) {
    return null;
  }

  const heritage =
    unwrapPortalNode(portalData.casePreAuthHeritage) ??
    unwrapPortalNode(portalData.casePreAuthOthersResponse);
  const healthIndia = unwrapPortalNode(portalData.casePreAuthHealthIndia);
  const fhpl = unwrapPortalNode(portalData.casePreAuthFhpl);

  const claimId =
    heritage?.claimId ?? healthIndia?.claimId ?? fhpl?.claimId ?? portalData.claimId;
  if (claimId == null) {
    return null;
  }
  const text = String(claimId).trim();
  return text || null;
};

export const resolvePortalDisplayClaimId = (params: {
  visitClaimId?: string | null;
  portalData?: Record<string, unknown> | null;
  ayushBPortalData?: Record<string, unknown> | null;
}): string | null => {
  const { visitClaimId, portalData, ayushBPortalData } = params;
  if (visitClaimId != null && String(visitClaimId).trim()) {
    return String(visitClaimId).trim();
  }

  const portal = portalData as Record<string, any> | null | undefined;
  const ayush = ayushBPortalData as Record<string, any> | null | undefined;

  const resolved =
    portal?.casePreAuthCghs?.claimId ??
    portal?.casePreAuthEchs?.casePreAuthEchsReferralResponse?.claimId ??
    portal?.casePreAuthEchs?.casePreAuthEchsEmergencyResponse?.claimId ??
    portal?.casePreAuthIhx?.claimId ??
    portal?.casePreAuthStarhealth?.claimId ??
    ayush?.casePreAuthOthersResponse?.claimId ??
    resolveHeritageHealthIndiaClaimId(portal ?? null);

  if (resolved == null) {
    return null;
  }
  const text = String(resolved).trim();
  return text || null;
};

export const dispatchHeritageOrHealthIndiaPortalData = (
  dispatch: AppDispatch,
  caseId: number,
  visitId: number,
  casesResponse?: {
    portalMetaDataNames?: string[];
    schemeMetaDataName?: string;
  },
) => {
  if (isHeritagePortalScheme(casesResponse)) {
    dispatch(getPortalDataHeritage({ caseId, visitId }));
    return true;
  }
  if (isHealthIndiaPortalScheme(casesResponse)) {
    dispatch(getPortalDataHealthIndia({ caseId, visitId }));
    return true;
  }
  if (isFhplPortalScheme(casesResponse)) {
    dispatch(getPortalDataFhpl({ caseId, visitId }));
    return true;
  }
  return false;
};
